import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import DynamicFieldLoader from './DynamicFieldLoader';
import { getServiceTypes } from '../../services/serviceType.service';
import intakeFormTypeService from '../../services/intakeFormType.service';

interface DynamicIntakeFormProps {
  serviceTypeId?: number;
  intakeFormTypeId?: number;
  onSubmit?: (formData: any) => void;
  onCancel?: () => void;
}

const DynamicIntakeForm: React.FC<DynamicIntakeFormProps> = ({
  serviceTypeId: initialServiceTypeId,
  intakeFormTypeId: initialIntakeFormTypeId,
  onSubmit,
  onCancel,
}) => {
  const [serviceTypeId, setServiceTypeId] = useState<number | undefined>(
    initialServiceTypeId
  );
  const [intakeFormTypeId, setIntakeFormTypeId] = useState<number | undefined>(
    initialIntakeFormTypeId
  );
  const [showAllSections, setShowAllSections] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);
  const [intakeFormTypes, setIntakeFormTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Load service types on component mount
  React.useEffect(() => {
    loadServiceTypes();
  }, []);

  // Load intake form types when service type changes
  React.useEffect(() => {
    if (serviceTypeId) {
      loadIntakeFormTypes(serviceTypeId);
    } else {
      setIntakeFormTypes([]);
    }
  }, [serviceTypeId]);

  const loadServiceTypes = async () => {
    try {
      const response = await getServiceTypes();
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Failed to load service types:', error);
    }
  };

  const loadIntakeFormTypes = async (serviceTypeId: number) => {
    try {
      const response =
        await intakeFormTypeService.getByServiceType(serviceTypeId);
      setIntakeFormTypes(response);
    } catch (error) {
      console.error('Failed to load intake form types:', error);
    }
  };

  const handleFormDataChange = (newFormData: any) => {
    setFormData(newFormData);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        console.log('Form Data:', formData);
        alert('Form submitted! Check console for data.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setFormData({});
      setServiceTypeId(undefined);
      setIntakeFormTypeId(undefined);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant='h4' gutterBottom>
        Dynamic Intake Form
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant='h6' gutterBottom>
          Configuration
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Service Type</InputLabel>
            <Select
              value={serviceTypeId || ''}
              onChange={e => setServiceTypeId(e.target.value as number)}
              label='Service Type'
            >
              <MenuItem value=''>
                <em>Select a service type</em>
              </MenuItem>
              {serviceTypes.map(serviceType => (
                <MenuItem key={serviceType.id} value={serviceType.id}>
                  {serviceType.name} ({serviceType.short_code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Intake Form Type</InputLabel>
            <Select
              value={intakeFormTypeId || ''}
              onChange={e => setIntakeFormTypeId(e.target.value as number)}
              label='Intake Form Type'
              disabled={!serviceTypeId}
            >
              <MenuItem value=''>
                <em>All form types</em>
              </MenuItem>
              {intakeFormTypes.map(intakeFormType => (
                <MenuItem key={intakeFormType.id} value={intakeFormType.id}>
                  {intakeFormType.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={showAllSections}
              onChange={e => setShowAllSections(e.target.checked)}
            />
          }
          label='Show all sections (even without mappings)'
        />
      </Box>

      {serviceTypeId ? (
        <Box>
          <Typography variant='h6' gutterBottom>
            Form Fields
            {intakeFormTypeId && (
              <Typography
                component='span'
                variant='body2'
                color='text.secondary'
                sx={{ ml: 1 }}
              >
                (Filtered by selected intake form type)
              </Typography>
            )}
          </Typography>

          <DynamicFieldLoader
            serviceTypeId={serviceTypeId}
            intakeFormTypeId={intakeFormTypeId}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            showAllSections={showAllSections}
          />

          <Box
            sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}
          >
            <Button variant='outlined' onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Form'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Alert severity='info'>
          Please select a service type to load the appropriate form fields.
        </Alert>
      )}
    </Paper>
  );
};

export default DynamicIntakeForm;
